import { SimpleListView } from '~/shared/components/ui';
import {
  ViewId,
  toCommaTypeString,
  convertLongToString,
  BASE_DIGIT_MILLION,
} from '~/shared/utils';
import { mtechnavi } from '~/shared/libs/clientsdk';
import Long from 'long';

interface ViewCertificateType {
  recordId: string; // Key
  displayName: string; // 認証名
  status: string; // 取得
  expirationDt: string; // 有効期限
}
interface CompanyConfirmationBasicProps {
  viewId: ViewId;
  profile?: mtechnavi.api.analysis.IProfile;
  certificates?: mtechnavi.api.analysis.ICertificate[];
  financials?: mtechnavi.api.analysis.IFinancial[];
}
export const CompanyConfirmationBasic = ({
  viewId,
  profile,
  certificates,
  financials,
}: CompanyConfirmationBasicProps) => {
  // 決算マスタ（最新の１件）
  const financial = financials
    ?.sort((a, b) => {
      const start = Number(a.periodStartDate || '0');
      const end = Number(b.periodStartDate || '0');
      return start < end ? 1 : -1;
    })
    .at(0);

  // 認証情報
  const certificateItems = (): ViewCertificateType[] => {
    if (!certificates) {
      return [];
    }
    const vals: ViewCertificateType[] = [];
    certificates.map((v) => {
      vals.push({
        recordId: v.recordId ?? '',
        displayName: v.displayName ? v.displayName : v.certificateName ?? '',
        status:
          (v.obtained ? 'あり' : 'なし') +
          (v.obtainPlan
            ? '（取得予定日：' + (v.obtainPlanDate ?? '') + '）'
            : ''),
        expirationDt: v.expirationDate ?? '',
      });
    });
    return vals;
  };

  const divideLong = (val: Long.Long | null | undefined) => {
    if (val) {
      return Long.fromValue(val).divide(BASE_DIGIT_MILLION);
    }
    return undefined;
  };

  return (
    <>
      <table className="basic-info-table">
        <tbody>
          <tr>
            <th className="root-header" rowSpan={8}>
              企業情報
            </th>
            <th>ふりがな</th>
            <td>{profile?.displayNameLangJaKana}</td>
            <th>英語名</th>
            <td>{profile?.displayNameLangEn}</td>
          </tr>

          <tr>
            <th>ホームページURL</th>
            <td colSpan={3}>
              <a
                target="_blank"
                rel="noreferrer"
                href={`${profile?.websiteUrl}`}
              >
                {profile?.websiteUrl}
              </a>
            </td>
          </tr>

          <tr>
            <th>事業内容</th>
            <td colSpan={3}>{profile?.mainBusiness}</td>
          </tr>

          <tr>
            <th>資本金(百万円)</th>
            <td>
              {toCommaTypeString(
                convertLongToString(divideLong(profile?.capitalStock))
              )}
            </td>
            <th>売上金額(百万円)</th>
            <td>
              {toCommaTypeString(
                convertLongToString(divideLong(financial?.netSalesAmount))
              )}
            </td>
          </tr>

          <tr>
            <th>従業員(人数)</th>
            <td>
              {toCommaTypeString(convertLongToString(profile?.employeesSize))}
            </td>
            <th>従業員(平均年齢)</th>
            <td>{profile?.employeesAverageAge || ''}</td>
          </tr>

          <tr>
            <th>主要株主</th>
            <td>{profile?.mainShareholder}</td>
            <th>主要取引銀行</th>
            <td>{profile?.mainBank || ''}</td>
          </tr>

          <tr>
            <th>主要仕入先</th>
            <td>{profile?.mainSupplier}</td>
            <th>主要納入先</th>
            <td>{profile?.mainCustomer}</td>
          </tr>

          <tr>
            <th>法人番号</th>
            <td>{profile?.companyNumber || ''}</td>
            <th>適格請求書番号</th>
            <td>{profile?.eligibleInvoiceAuthorizationNumber || ''}</td>
          </tr>

          <tr>
            <th className="root-header" rowSpan={2}>
              代表者
            </th>
            <th>氏名</th>
            <td>{profile?.representative?.displayNameLangJa}</td>
            <th>ふりがな</th>
            <td>{profile?.representative?.displayNameLangJaKana}</td>
          </tr>

          <tr>
            <th>役職</th>
            <td>{profile?.representative?.position}</td>
            <th>生年月日</th>
            <td>{profile?.representative?.birthDate || ''}</td>
          </tr>

          <tr>
            <th className="root-header" rowSpan={3}>
              本社情報
            </th>
            <th>都道府県</th>
            <td colSpan={3}>{profile?.address?.regionName || ''}</td>
          </tr>

          <tr>
            <th>住所</th>
            <td colSpan={3}>{profile?.address?.addressLines || ''}</td>
          </tr>

          <tr>
            <th>電話番号</th>
            <td colSpan={3}>{profile?.phoneNumber}</td>
          </tr>
        </tbody>
      </table>

      <div className="input-line">
        <div className="item-group-100">
          <div className="w-50">
            <div className="certificates">
              <span>認証</span>
              <SimpleListView
                data={certificateItems()}
                viewOptions={{
                  omitFooter: true,
                  keyColumn: 'id',
                  columns: [
                    {
                      header: { viewId: viewId, id: 'displayName' },
                      propertyName: 'displayName',
                      width: '41%',
                    },
                    {
                      header: { viewId: viewId, id: 'obtained' },
                      propertyName: 'status',
                      width: '41%',
                    },
                    {
                      header: { viewId: viewId, id: 'expirationDt' },
                      propertyName: 'expirationDt',
                      width: '18%',
                    },
                  ],
                }}
                actionOptions={{}}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
